// Pure, framework-free helpers that turn an Apify actor input schema (v1)
// into a simple form model. ApifyEditor imports these so it only deals with
// rendering, not schema interpretation.
//
// Schema shape (docs.apify.com/platform/actors/development/actor-definition/
// input-schema/specification/v1):
//   { title, type: 'object', schemaVersion: 1,
//     properties: { <key>: { title, type, description, editor, default,
//                            prefill, enum, enumTitles, ... } },
//     required: [<key>, ...] }

function getProperties(schema) {
  return (schema && schema.properties && typeof schema.properties === 'object') ? schema.properties : {};
}

// Ordered field descriptors. Apify preserves property order in the JSON and
// Object.keys keeps that order for string keys, so the form matches the docs.
function getFields(schema) {
  var props = getProperties(schema);
  var required = Array.isArray(schema && schema.required) ? schema.required : [];
  return Object.keys(props).map(function (key) {
    return { key: key, prop: props[key] || {}, required: required.indexOf(key) >= 0 };
  });
}

// Collapse a field's (type, editor) into the small set of widgets ApifyEditor
// renders. Arrays / objects and anything unrecognized fall back to raw JSON.
function widgetFor(prop) {
  var type = String((prop && prop.type) || 'string');
  var editor = String((prop && prop.editor) || '');
  if (type === 'boolean') return 'checkbox';
  if (type === 'integer' || type === 'number') return 'number';
  if (Array.isArray(prop && prop.enum) && prop.enum.length) return 'select';
  if (type === 'string') {
    if (editor === 'textarea' || editor === 'javascript' || editor === 'python' || editor === 'json') return 'textarea';
    return 'text';
  }
  // Arrays and (non-proxy) objects get the friendly recursive tree editor:
  // arrays become add/remove lists, objects a labelled tree. Proxy config
  // stays raw JSON — its nested shape doesn't map cleanly to the tree.
  if (type === 'array') return 'tree';
  if (type === 'object' && editor !== 'proxy') return 'tree';
  return 'json';
}

// A field's starting value: Apify's `prefill` (example) first, then `default`,
// else undefined (left unset so we don't send empty noise to the actor).
function initialValue(prop) {
  if (prop && prop.prefill !== undefined) return prop.prefill;
  if (prop && prop.default !== undefined) return prop.default;
  return undefined;
}

// Build the initial input object when an actor is picked.
function seedInput(schema) {
  var out = {};
  getFields(schema).forEach(function (f) {
    var v = initialValue(f.prop);
    if (v !== undefined) out[f.key] = v;
  });
  return out;
}

// Apify field titles/descriptions are authored as HTML (with <b>, <br>,
// <a href>, <code>) and sometimes markdown links. We render them as plain
// text — never as HTML — so strip the markup safely: unwrap markdown links to
// their text, turn breaks/blocks into newlines, drop remaining tags, and
// decode the few common entities. Returns clean, readable text.
function safeCodePoint(code) {
  try { return Number.isFinite(code) ? String.fromCodePoint(code) : ''; }
  catch (_) { return ''; }
}

function htmlToPlainText(str) {
  if (!str) return '';
  var s = String(str);
  s = s.replace(/\[([^\]]+)\]\([^)]*\)/g, '$1');      // [text](url) -> text
  s = s.replace(/<br\s*\/?>/gi, '\n');                 // <br> -> newline
  s = s.replace(/<\/(p|div|li|ul|ol)>/gi, '\n');       // block ends -> newline
  s = s.replace(/<[^>]+>/g, '');                       // drop all remaining tags
  s = s
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    // Numeric entities: &#9888; (decimal) and &#x26A0; (hex) — covers the ⚠
    // and similar symbols Apify sprinkles through its help text.
    .replace(/&#x([0-9a-f]+);/gi, function (_, hex) { return safeCodePoint(parseInt(hex, 16)); })
    .replace(/&#(\d+);/g, function (_, dec) { return safeCodePoint(parseInt(dec, 10)); });
  s = s.replace(/[ \t]+\n/g, '\n').replace(/\n{3,}/g, '\n\n').replace(/[ \t]{2,}/g, ' ').trim();
  return s;
}

module.exports = { getFields, widgetFor, seedInput, htmlToPlainText };
